import { motion } from 'framer-motion'
import { Heart } from 'lucide-react'
import { Link } from 'react-router-dom'
import textBanner from '../../assets/images/text-banner-black-min.png'
import { trackExternalLinkClick } from '../../config/analyticsEvents'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const links = {
    product: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing' },
    ],
    api: [
      { name: 'Dashboard', to: '/dashboard', isRouterLink: true },
      { name: 'Get API Key', to: '/auth', isRouterLink: true },
    ],
    resources: [
      { name: 'iOS Guidelines', href: 'https://developer.apple.com/design/human-interface-guidelines/app-icons' },
      { name: 'Android Guidelines', href: 'https://developer.android.com/guide/practices/ui_guidelines/icon_design' },
    ],
    legal: [
      { name: 'Privacy Policy', to: '/privacy-policy', isRouterLink: true },
      { name: 'Terms of Service', to: '/terms-of-service', isRouterLink: true },
      { name: 'Cookie Policy', to: '/cookie-policy', isRouterLink: true },
      { name: 'DPA', to: '/dpa', isRouterLink: true },
    ]
  }

  const socialLinks = [
    // { name: 'GitHub', href: '#', icon: Github },
    // { name: 'Twitter', href: '#', icon: Twitter },
  ]

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0 py-12">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-8">
          {/* Brand Section - Left */}
          <div className="lg:max-w-sm">
            <motion.div
              className="flex items-center mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <img 
                  src={textBanner} 
                  alt="EasyAppIcon API" 
                  className="h-8 brightness-0 invert"
                />
                <span className="absolute -top-1 -right-8 bg-[#df6e57] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  API
                </span>
              </div>
            </motion.div>
            
            <motion.p
              className="text-gray-400 mb-6 max-w-md leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              The modern, fast, and secure way to generate app icons for iOS and Android. 
              Created with ❤️ for developers who value quality and simplicity.
            </motion.p>

            {/* Support Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {/* Donation Button */}
              <div className="flex justify-start">
                <a 
                  href="https://www.buymeacoffee.com/dg43aLHr6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackExternalLinkClick('buy_me_a_coffee', 'https://www.buymeacoffee.com/dg43aLHr6')}
                  className="hover:scale-105 transition-transform duration-200 cursor-pointer"
                >
                  <img 
                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" 
                    alt="Buy Me A Coffee" 
                    className="h-10 w-auto"
                  />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Links Sections - Right */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-12 gap-y-8">
            {Object.entries(links).map(([category, categoryLinks], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <h3 className="text-white font-semibold mb-4 capitalize">
                  {category === 'legal' ? 'Legal' : category}
                </h3>
                <ul className="space-y-3">
                  {categoryLinks.map((link) => (
                    <li key={link.name}>
                      {link.isRouterLink ? (
                        <Link
                          to={link.to}
                          className="text-gray-400 hover:text-white transition-colors duration-200 text-sm cursor-pointer"
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          {...(link.href?.startsWith('http') ? {
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            onClick: () => trackExternalLinkClick(link.name, link.href)
                          } : {})}
                          className="text-gray-400 hover:text-white transition-colors duration-200 text-sm cursor-pointer"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <motion.div
              className="text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              © {currentYear} EasyAppIcon API. Built by{' '}
              <a
                href="https://easyio.dev"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLinkClick('EasyIO.dev', 'https://easyio.dev')}
                className="hover:text-white transition-colors duration-200"
              >
                EasyIO.dev
              </a>
              .
            </motion.div>

            {/* Social Links */}
            <motion.div
              className="flex items-center space-x-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-gray-400 hover:text-white transition-colors duration-200 cursor-pointer"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </motion.div>

            {/* Made with Love */}
            <motion.div
              className="flex items-center space-x-1 text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500" />
              <span>for developers</span>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
